import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { Spinner, Button, Card } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import styles from './ProtectedRoute.module.css';

interface ProtectedRouteProps {
  redirectToLogin?: boolean;
  redirectPath?: string;
  requireStaff?: boolean;
  title?: string;
  message?: string;
}

export function ProtectedRoute({
  redirectToLogin = false,
  redirectPath = ROUTES.login,
  requireStaff = false,
  title = 'Authentication Required',
  message = 'Please log in to access this page.',
}: ProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLogged, isLoading } = useAuthStore();
  const getUser = useAuthStore((s) => s.getUser);

  if (isLoading && !isLogged) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isLogged) {
    if (redirectToLogin) {
      return <Navigate to={redirectPath} state={{ from: location }} replace />;
    }

    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <div className={styles.content}>
            <h2>{title}</h2>
            <p>{message}</p>
            <div className={styles.buttons}>
              <Button
                variant="primary"
                onClick={() => navigate(ROUTES.login, { state: { from: location } })}
              >
                Login
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // User is logged in but we require staff
  if (requireStaff) {
    const user = getUser();
    if (!user?.is_staff) {
      return <Navigate to={ROUTES.pendingApproval} replace />;
    }
  }

  return <Outlet />;
}
